import React, { useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { useTrack } from "~/components/build/Show/lib/useTrack";
import classNames from "classnames";
import { State } from "~/lib/pipeline";

interface Props {
  state?: State | null;
  to: string;
  type?: string;
  children: React.ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  active?: boolean;
  failed?: boolean;
  depth?: number;
  title?: string;
  disabled?: boolean;
}

/**
 * A themed sidebar link that is scrolled into view when active.
 */
export const StepLink = ({
  to,
  type,
  className,
  children,
  onClick,
  active,
  depth = 0,
  failed,
  title,
  disabled,
  state,
}: Props) => {
  const track = useTrack();
  const scrollRef = useRef<HTMLAnchorElement>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      track("Build Sidebar Step Clicked", { type });

      onClick?.(event);

      if (active && !event.metaKey) {
        event.preventDefault();
        navigate(pathname);
        return;
      }
    },
    [active, pathname],
  );

  return (
    <Link
      title={title}
      aria-current={active ? "step" : undefined}
      onClick={handleClick}
      to={to}
      ref={scrollRef}
      data-test-id="step-link"
      data-step-state={state}
      aria-disabled={disabled}
      style={{ contentVisibility: "auto" }}
      className={twMerge(
        "cursor-not-allowed:aria-disabled",
        "text-charcoal-700 flex min-w-0 flex-auto items-baseline gap-1 rounded-md px-2 text-sm no-underline hover:text-inherit hover:no-underline focus:text-inherit focus:no-underline active:no-underline",
        // Blended color: uses purple-100 with 50% opacity in light mode, purple-700 with 25% opacity in dark mode, and other opacity values (e.g., 15% in dark mode for failed links) as needed.
        "aria-[current=step]:bg-purple-100/50-white hover:bg-purple-100/50-white group-hover:bg-purple-100/50-white dark:hover:bg-purple-700/25 dark:group-hover:bg-purple-700/25",
        classNames({
          // Blended color to have the appearance of red-100 with opacity 50% in light mode, 15% in dark mode red background, and 25% in dark mode hover
          "[&:not([aria-current=step])]:bg-red-100/50-white [&:not([aria-current=step])]:hover:bg-purple-100/50-white dark:[&:not([aria-current=step])]:bg-red-700/15 dark:[&:not([aria-current=step])]:hover:bg-purple-700/25":
            failed,

          // Group appearance of consecutive failed links by removing round borders, keep the active job rounded.
          "failed aria-[current=step]:!rounded-md [&.failed:first-child]:rounded-t-md [&.failed:not(:has(+_.failed)):not(:has(~_.failed_+_:not(.failed)))]:rounded-b-md [&.failed]:rounded-none":
            failed,

          // Active appearance
          "relative ring-2 ring-inset ring-purple-600": active,

          // Fade out when waiting for dependencies and not active
          "[&>*]:opacity-45 [&>*]:hover:opacity-100":
            state === State.WaitingForDependencies && !active,

          // Indentation based on depth
          "pl-[calc(1.5rem_+_2px)]": depth === 1, // + 2px to account for chevron padding
          "pl-[calc(2.5rem_+_4px)]": depth === 2, // + 4px to account for chevron padding
        }),
        className,
      )}
    >
      {children}
    </Link>
  );
};
